/********************* Setup *********************/

// tick used by the set's locked piece checkboxes
const QUICK_ADD_TICK_ICON = `<svg class="quickAddIcon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"></path></svg>`;

// info icon in front of the "only comes in" note
const QUICK_ADD_INFO_ICON = `<svg class="quickAddBodyOptionsGroupNoteIcon quickAddIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5.5"></path><path d="M12 7.6v0.2"></path></svg>`;

// the card data being shown (see snippets/theme-quick-add-json.liquid) and what runs once it is in the cart
let quickAddData = null;
let quickAddOnAdded = null;

// product mode keeps one value per option, set mode keeps the shared colour and size keys, which pieces are ticked
// and the size and count picked for each piece sold by quantity (pillowcases)
let quickAddSelected = [];
let quickAddSetColor = null;
let quickAddSetSize = null;
let quickAddSetPicked = [];
let quickAddSetPieceOptions = [];

// a product's price override shows in the header until the shopper changes an option
let quickAddShowPriceOverride = false;

// escape product text before it goes into markup
function quickAddEscape(text) {
    return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// a colour swatch is a flat colour or gradient from pdp_color_swatch, or an image from pdp_color_image
function quickAddSwatchStyle(value) {
    if (value.image) {
        return `background-image: url(${value.image});`;
    }
    if (value.swatch) {
        return `background: ${value.swatch};`;
    }
    return "";
}

// list values as "A", "A and B" or "A, B and C"
function quickAddJoin(values) {
    if (values.length < 2) {
        return values.join("");
    }
    return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
}

/********************* Product Mode *********************/

// the variant with exactly these option values
function quickAddFindVariant(options) {
    return quickAddData.variants.find(variant => variant.options.every((value, index) => value === options[index]));
}

// after a pick, keep the other choices if that variant can be bought, otherwise move to the closest one in stock
function quickAddSelectValue(index, value) {
    quickAddSelected[index] = value;

    const current = quickAddFindVariant(quickAddSelected);
    if (current?.available) {
        return;
    }

    // in stock variants with the picked value, preferring the most other choices kept
    const candidates = quickAddData.variants.filter(variant => variant.options[index] === value && variant.available);
    if (candidates.length === 0) {
        return;
    }
    const score = variant => variant.options.filter((option, optionIndex) => option === quickAddSelected[optionIndex]).length;
    const best = candidates.reduce((top, variant) => score(variant) > score(top) ? variant : top);
    quickAddSelected = [...best.options];
}

// swatches for a colour option that has swatch data, priced pills for everything else
function quickAddRenderProductOptions() {
    const colorIndex = quickAddData.options.findIndex(option => option.color);
    let html = "";

    quickAddData.options.forEach((option, index) => {
        const selectedValue = quickAddSelected[index];
        const hasSwatches = option.color && option.values.some(value => value.swatch || value.image);

        // swatches, disabled when nothing in that colour is in stock
        if (hasSwatches) {
            let swatchesHTML = "";
            option.values.forEach(value => {
                const inStock = quickAddData.variants.some(variant => variant.options[index] === value.value && variant.available);
                const label = quickAddEscape(value.value);

                let stateClass = "";
                if (value.value === selectedValue) {
                    stateClass = " active";
                }
                let disabledAttribute = "";
                if (!inStock) {
                    disabledAttribute = " disabled";
                    stateClass += " soldOut";
                }

                swatchesHTML += `<button class="quickAddBodyOptionsGroupSwatchesItem${stateClass}" type="button" data-index="${index}" data-value="${label}" aria-label="${label}" title="${label}" aria-pressed="${value.value === selectedValue}" style="${quickAddSwatchStyle(value)}"${disabledAttribute}></button>`;
            });

            html += `
                <div class="quickAddBodyOptionsGroup" role="group" aria-label="${quickAddEscape(option.name)}">
                    <div class="quickAddBodyOptionsGroupHeader">
                        <span class="quickAddBodyOptionsGroupHeaderName">${quickAddEscape(option.name)}</span>
                        <span class="quickAddBodyOptionsGroupHeaderValue">${quickAddEscape(selectedValue)}</span>
                    </div>
                    <div class="quickAddBodyOptionsGroupSwatches">${swatchesHTML}</div>
                </div>
            `;
            return;
        }

        // the variant each value would pick with the other choices kept
        const pills = option.values.map(value => {
            const options = [...quickAddSelected];
            options[index] = value.value;
            return {value: value.value, variant: quickAddFindVariant(options)};
        });

        // prices only help when they differ between values
        const prices = pills.filter(pill => pill.variant).map(pill => pill.variant.price);
        const showPrices = new Set(prices).size > 1;

        let pillsHTML = "";
        const missing = [];
        pills.forEach(pill => {
            // values with no variant in the current colour are left out and explained in the note below
            if (!pill.variant) {
                missing.push(pill.value);
                return;
            }

            const label = quickAddEscape(pill.value);
            let stateClass = "";
            if (pill.value === selectedValue) {
                stateClass = " active";
            }

            // sold out pills are struck through and can't be picked
            let subHTML = "";
            if (showPrices) {
                subHTML = `<span class="quickAddBodyOptionsGroupPillsItemPrice">${price(pill.variant.price, true)}</span>`;
            }
            let disabledAttribute = "";
            let ariaLabel = label;
            if (!pill.variant.available) {
                stateClass += " soldOut";
                disabledAttribute = " disabled";
                ariaLabel = `${label}, ${quickAddEscape(document.querySelector("#quickAdd").dataset.soldOutText)}`;
                subHTML = `<span class="quickAddBodyOptionsGroupPillsItemPrice">${quickAddEscape(document.querySelector("#quickAdd").dataset.soldOutText)}</span>`;
            }

            pillsHTML += `
                <button class="quickAddBodyOptionsGroupPillsItem${stateClass}" type="button" data-index="${index}" data-value="${label}" aria-label="${ariaLabel}" aria-pressed="${pill.value === selectedValue}"${disabledAttribute}>
                    <span class="quickAddBodyOptionsGroupPillsItemName">${label}</span>
                    ${subHTML}
                </button>
            `;
        });

        // say which colours a left out value does come in
        let noteHTML = "";
        if (colorIndex > -1 && colorIndex !== index) {
            let noteText = "";
            missing.forEach(value => {
                const colors = [];
                quickAddData.variants.forEach(variant => {
                    if (variant.options[index] !== value || colors.includes(variant.options[colorIndex])) {
                        return;
                    }
                    colors.push(variant.options[colorIndex]);
                });
                if (colors.length === 0) {
                    return;
                }
                noteText += `${value} comes in ${quickAddJoin(colors)} only. `;
            });
            if (noteText) {
                noteHTML = `<p class="quickAddBodyOptionsGroupNote">${QUICK_ADD_INFO_ICON}${quickAddEscape(noteText.trim())}</p>`;
            }
        }

        html += `
            <div class="quickAddBodyOptionsGroup" role="group" aria-label="${quickAddEscape(option.name)}">
                <div class="quickAddBodyOptionsGroupHeader">
                    <span class="quickAddBodyOptionsGroupHeaderName">${quickAddEscape(option.name)}</span>
                    <span class="quickAddBodyOptionsGroupHeaderValue">${quickAddEscape(selectedValue)}</span>
                </div>
                <div class="quickAddBodyOptionsGroupPills">${pillsHTML}</div>
                ${noteHTML}
            </div>
        `;
    });

    document.querySelector("#quickAddBodyOptions").innerHTML = html;
}

function quickAddRenderProduct() {
    const dialog = document.querySelector("#quickAdd");
    const variant = quickAddFindVariant(quickAddSelected);

    // the variant's own image when it has one
    quickAddRenderImage(variant?.image || quickAddData.image);

    // header price, the override until an option changes, blank when the combination doesn't exist
    let priceText = "";
    if (quickAddShowPriceOverride) {
        priceText = quickAddData.price_override;
    } else if (variant) {
        priceText = price(variant.price);
    }
    document.querySelector("#quickAddBodyHeaderTextPriceValue").textContent = priceText;
    document.querySelector("#quickAddBodyHeaderTextPriceCount").textContent = "";

    quickAddRenderProductOptions();

    // add button with the price, or sold out
    const atc = document.querySelector("#quickAddBodyFooterATC");
    atc.disabled = !variant?.available;
    atc.textContent = dialog.dataset.soldOutText;
    if (variant?.available) {
        atc.textContent = `${dialog.dataset.buttonText} · ${price(variant.price)}`;
    }
}

/********************* Set Mode *********************/

// pieces with fewer than two sizes (pillowcases) match on colour alone
function quickAddIsSizeless(piece) {
    const sizes = new Set(piece.variants.map(variant => variant.size).filter(size => size !== null));
    return sizes.size < 2;
}

// a quantity piece comes in pairs like the sheet selector: 2x and 4x of each size, or 2x, 4x and 6x when it has one size
function quickAddPieceOptions(piece) {
    const options = [];
    if (piece.sizes.length > 1) {
        [1, 2].forEach(quantity => {
            piece.sizes.forEach(size => {
                options.push({
                    size: size.value,
                    quantity: quantity,
                    label: `${quantity * 2}x ${size.label} ${piece.title}`
                });
            });
        });
        return options;
    }
    [1, 2, 3].forEach(quantity => {
        options.push({size: null, quantity: quantity, label: `${quantity * 2}x ${piece.title}`});
    });
    return options;
}

// the cheapest variant in the colour (and the option's size), preferring one in stock so a sold out match still shows as sold out
function quickAddOptionVariant(piece, option, color) {
    const matching = piece.variants.filter(variant =>
        (option.size === null || variant.size === option.size) &&
        (variant.color === color || variant.color === null)
    );
    let pool = matching.filter(variant => variant.available);
    if (pool.length === 0) {
        pool = matching;
    }
    return [...pool].sort((first, second) => first.price - second.price)[0] || null;
}

// first option of a quantity piece that's in stock in the colour, or the first option when none are
function quickAddFirstPieceOption(piece, color) {
    const options = quickAddPieceOptions(piece);
    return options.find(option => quickAddOptionVariant(piece, option, color)?.available) || options[0];
}

// the option a quantity piece is on, the first in stock one until the shopper picks
function quickAddPieceOption(piece, color) {
    return quickAddSetPieceOptions[quickAddData.pieces.indexOf(piece)] || quickAddFirstPieceOption(piece, color);
}

// how many of a piece go in the cart, a count of pairs for a quantity piece
function quickAddPieceQuantity(piece) {
    if (!piece.quantities) {
        return 1;
    }
    return quickAddPieceOption(piece, quickAddSetColor).quantity;
}

// the variant one piece resolves to for the shared size and colour, a piece without that option ignores it
// a quantity piece ignores the shared size and uses its own option
function quickAddPieceVariant(piece, size, color) {
    if (piece.quantities) {
        return quickAddOptionVariant(piece, quickAddPieceOption(piece, color), color);
    }

    const sizeless = quickAddIsSizeless(piece);
    return piece.variants.find(variant =>
        (sizeless || variant.size === size || variant.size === null) &&
        (variant.color === color || variant.color === null)
    ) || null;
}

// the ticked pieces only
function quickAddSetPickedPieces() {
    return quickAddData.pieces.filter((piece, index) => quickAddSetPicked[index]);
}

// a combination only counts when every ticked piece is in stock (every piece while none are ticked, so the options still show stock)
function quickAddSetAvailable(size, color) {
    let pieces = quickAddSetPickedPieces();
    if (pieces.length === 0) {
        pieces = quickAddData.pieces;
    }
    return pieces.every(piece => {
        // a quantity piece moves to another pair when its own is sold out in a colour, so any pair in stock will do
        if (piece.quantities) {
            return quickAddOptionVariant(piece, quickAddFirstPieceOption(piece, color), color)?.available;
        }
        return quickAddPieceVariant(piece, size, color)?.available;
    });
}

// the cheapest single item in stock in the colour, shown as the from price while nothing is ticked
function quickAddSetLowestPrice() {
    const prices = [];
    quickAddData.pieces.forEach(piece => {
        piece.variants.forEach(variant => {
            if (variant.available && (variant.color === quickAddSetColor || variant.color === null)) {
                prices.push(variant.price);
            }
        });
    });
    if (prices.length === 0) {
        return null;
    }
    return Math.min(...prices);
}

// a quantity piece whose picked option is sold out in a new colour moves to the first option that isn't
function quickAddSetCheckPieceOptions() {
    quickAddData.pieces.forEach((piece, index) => {
        if (!piece.quantities || !quickAddSetPieceOptions[index]) {
            return;
        }
        if (!quickAddPieceVariant(piece, null, quickAddSetColor)?.available) {
            quickAddSetPieceOptions[index] = quickAddFirstPieceOption(piece, quickAddSetColor);
        }
    });
}

// shared colour and size keys, a set without one of the options uses null for it
function quickAddSetColors() {
    if (quickAddData.colors.length === 0) {
        return [{value: null, label: ""}];
    }
    return quickAddData.colors;
}

function quickAddSetSizes() {
    if (quickAddData.sizes.length === 0) {
        return [{value: null, label: ""}];
    }
    return quickAddData.sizes;
}

// first size in stock for a colour, or null when the colour is sold out in every size
function quickAddFirstSetSize(color) {
    return quickAddSetSizes().find(size => quickAddSetAvailable(size.value, color)) || null;
}

function quickAddRenderSetOptions() {
    const dialog = document.querySelector("#quickAdd");
    let html = "";

    // colour swatches, disabled when no size can fill the ticked pieces
    if (quickAddData.colors.length > 0) {
        let selectedLabel = "";
        let swatchesHTML = "";
        quickAddData.colors.forEach(color => {
            const label = quickAddEscape(color.label);
            let stateClass = "";
            if (color.value === quickAddSetColor) {
                stateClass = " active";
                selectedLabel = label;
            }
            let disabledAttribute = "";
            if (!quickAddFirstSetSize(color.value)) {
                stateClass += " soldOut";
                disabledAttribute = " disabled";
            }

            swatchesHTML += `<button class="quickAddBodyOptionsGroupSwatchesItem${stateClass}" type="button" data-group="color" data-value="${quickAddEscape(color.value)}" aria-label="${label}" title="${label}" aria-pressed="${color.value === quickAddSetColor}" style="${quickAddSwatchStyle(color)}"${disabledAttribute}></button>`;
        });

        html += `
            <div class="quickAddBodyOptionsGroup" role="group" aria-label="Colour">
                <div class="quickAddBodyOptionsGroupHeader">
                    <span class="quickAddBodyOptionsGroupHeaderName">Colour</span>
                    <span class="quickAddBodyOptionsGroupHeaderValue">${selectedLabel}</span>
                </div>
                <div class="quickAddBodyOptionsGroupSwatches">${swatchesHTML}</div>
            </div>
        `;
    }

    // bed size pills, sold out when any ticked piece is out in this colour
    if (quickAddData.sizes.length > 0) {
        let selectedLabel = "";
        let pillsHTML = "";
        quickAddData.sizes.forEach(size => {
            const label = quickAddEscape(size.label);
            let stateClass = "";
            if (size.value === quickAddSetSize) {
                stateClass = " active";
                selectedLabel = label;
            }
            let disabledAttribute = "";
            let ariaLabel = label;
            if (!quickAddSetAvailable(size.value, quickAddSetColor)) {
                stateClass += " soldOut";
                disabledAttribute = " disabled";
                ariaLabel = `${label}, ${quickAddEscape(dialog.dataset.soldOutText)}`;
            }

            pillsHTML += `
                <button class="quickAddBodyOptionsGroupPillsItem${stateClass}" type="button" data-group="size" data-value="${quickAddEscape(size.value)}" aria-label="${ariaLabel}" aria-pressed="${size.value === quickAddSetSize}"${disabledAttribute}>
                    <span class="quickAddBodyOptionsGroupPillsItemName">${label}</span>
                </button>
            `;
        });

        html += `
            <div class="quickAddBodyOptionsGroup" role="group" aria-label="${quickAddEscape(dialog.dataset.setSizeTitle)}">
                <div class="quickAddBodyOptionsGroupHeader">
                    <span class="quickAddBodyOptionsGroupHeaderName">${quickAddEscape(dialog.dataset.setSizeTitle)}</span>
                    <span class="quickAddBodyOptionsGroupHeaderValue">${selectedLabel}</span>
                </div>
                <div class="quickAddBodyOptionsGroupPills">${pillsHTML}</div>
            </div>
        `;
    }

    document.querySelector("#quickAddBodyOptions").innerHTML = html;
}

// every piece with a tick box and the variant it resolves to, a ticked quantity piece shows its size and count pills below
function quickAddRenderSetPieces() {
    const soldOutText = document.querySelector("#quickAdd").dataset.soldOutText;
    let html = "";

    quickAddData.pieces.forEach((piece, index) => {
        const variant = quickAddPieceVariant(piece, quickAddSetSize, quickAddSetColor);
        const quantity = quickAddPieceQuantity(piece);
        const title = quickAddEscape(piece.title);

        let iconHTML = "";
        if (piece.icon) {
            iconHTML = `<img class="quickAddBodyPiecesListItemIcon" src="${piece.icon}" alt="" width="30" height="30">`;
        }

        // "Queen · Stone", or sold out when this piece can't be had in the combination
        let variantText = soldOutText;
        let priceHTML = "";
        if (variant) {
            variantText = variant.label;
            priceHTML = `<span class="quickAddBodyPiecesListItemPrice">${price(variant.price * quantity, true)}</span>`;
        }
        if (variant && !variant.available) {
            variantText = `${variantText}, ${soldOutText}`;
        }

        // pairs the shopper can pick, sold out ones struck through
        let optionsHTML = "";
        if (piece.quantities && quickAddSetPicked[index]) {
            const selectedOption = quickAddPieceOption(piece, quickAddSetColor);
            let pillsHTML = "";
            quickAddPieceOptions(piece).forEach((option, optionIndex) => {
                const optionVariant = quickAddOptionVariant(piece, option, quickAddSetColor);
                const label = quickAddEscape(option.label);
                const selected = option.size === selectedOption.size && option.quantity === selectedOption.quantity;

                let stateClass = "";
                if (selected) {
                    stateClass = " active";
                }
                let disabledAttribute = "";
                let ariaLabel = label;
                if (!optionVariant?.available) {
                    stateClass += " soldOut";
                    disabledAttribute = " disabled";
                    ariaLabel = `${label}, ${quickAddEscape(soldOutText)}`;
                }

                pillsHTML += `
                    <button class="quickAddBodyOptionsGroupPillsItem${stateClass}" type="button" data-index="${index}" data-option="${optionIndex}" aria-label="${ariaLabel}" aria-pressed="${selected}"${disabledAttribute}>
                        <span class="quickAddBodyOptionsGroupPillsItemName">${label}</span>
                    </button>
                `;
            });
            optionsHTML = `<div class="quickAddBodyPiecesListItemOptions quickAddBodyOptionsGroupPills" role="group" aria-label="${title}">${pillsHTML}</div>`;
        }

        let stateClass = "";
        let checkedAttribute = "";
        if (quickAddSetPicked[index]) {
            stateClass = " active";
            checkedAttribute = " checked";
        }

        html += `
            <div class="quickAddBodyPiecesListItem${stateClass}">
                <label class="quickAddBodyPiecesListItemInner">
                    <input class="quickAddBodyPiecesListItemInput" type="checkbox" data-index="${index}" aria-label="${title}"${checkedAttribute}>
                    <span class="quickAddBodyPiecesListItemBox" aria-hidden="true">${QUICK_ADD_TICK_ICON}</span>
                    ${iconHTML}
                    <span class="quickAddBodyPiecesListItemText">
                        <span class="quickAddBodyPiecesListItemTextName">${title}</span>
                        <span class="quickAddBodyPiecesListItemTextVariant">${quickAddEscape(variantText)}</span>
                    </span>
                    ${priceHTML}
                </label>
                ${optionsHTML}
            </div>
        `;
    });

    document.querySelector("#quickAddBodyPiecesList").innerHTML = html;
}

function quickAddRenderSet() {
    const dialog = document.querySelector("#quickAdd");

    const picked = quickAddSetPickedPieces();
    const pieceCount = quickAddData.pieces.length;

    // total of the ticked pieces, one of each or the picked count of a quantity piece
    let total = 0;
    picked.forEach(piece => {
        total += (quickAddPieceVariant(piece, quickAddSetSize, quickAddSetColor)?.price ?? 0) * quickAddPieceQuantity(piece);
    });

    // first ticked piece in the chosen colour, falling back to the set image
    const imagePiece = picked[0] || quickAddData.pieces[0];
    const imageVariant = quickAddPieceVariant(imagePiece, quickAddSetSize, quickAddSetColor);
    quickAddRenderImage(imageVariant?.image || quickAddData.image);

    // "3 pieces" for the whole set, "2 of 3 pieces" for part of it, and the override or cheapest single item while nothing is ticked
    let priceText = price(total);
    let countText = `${pieceCount} pieces`;
    if (picked.length === 0) {
        priceText = "";
        countText = "";
        const lowest = quickAddSetLowestPrice();
        if (quickAddData.price_override) {
            priceText = `From ${quickAddData.price_override}`;
        } else if (lowest !== null) {
            priceText = `From ${price(lowest)}`;
        }
    } else if (picked.length < pieceCount) {
        countText = `${picked.length} of ${pieceCount} pieces`;
    }
    document.querySelector("#quickAddBodyHeaderTextPriceValue").textContent = priceText;
    document.querySelector("#quickAddBodyHeaderTextPriceCount").textContent = countText;

    quickAddRenderSetOptions();
    quickAddRenderSetPieces();

    // add button: the set text for every piece, a piece count for part of the set, sold out, or a prompt when nothing is ticked
    const atc = document.querySelector("#quickAddBodyFooterATC");
    const available = picked.length > 0 && quickAddSetAvailable(quickAddSetSize, quickAddSetColor);
    atc.disabled = !available;
    atc.textContent = dialog.dataset.soldOutText;
    if (picked.length === 0) {
        atc.textContent = dialog.dataset.setEmptyText;
    } else if (available && picked.length === pieceCount) {
        atc.textContent = `${dialog.dataset.setButtonText} · ${price(total)}`;
    } else if (available && picked.length === 1) {
        atc.textContent = `Add 1 piece · ${price(total)}`;
    } else if (available) {
        atc.textContent = `Add ${picked.length} pieces · ${price(total)}`;
    }
}

/********************* Render *********************/

// the desktop image column and the mobile thumbnail show the same picture
function quickAddRenderImage(src) {
    ["#quickAddMediaImage", "#quickAddBodyHeaderImageImg"].forEach(selector => {
        const image = document.querySelector(selector);
        if (src) {
            image.src = src;
            return;
        }
        image.removeAttribute("src");
    });
}

function quickAddRender() {
    if (quickAddData.type === "set") {
        quickAddRenderSet();
        return;
    }
    quickAddRenderProduct();
}

/********************* Open and Close *********************/

// open with one card's data, onAdded runs once the items are in the cart (each cart re-renders itself)
function openQuickAdd(data, onAdded) {
    const dialog = document.querySelector("#quickAdd");
    quickAddData = data;
    quickAddOnAdded = onAdded;

    // start from the first in stock choice, a set starts with nothing ticked unless its card asks for every piece
    if (data.type === "set") {
        quickAddSetPicked = data.pieces.map(() => data.start_ticked);
        quickAddSetPieceOptions = data.pieces.map(() => null);

        const firstColor = quickAddSetColors().find(color => quickAddFirstSetSize(color.value)) || quickAddSetColors()[0];
        quickAddSetColor = firstColor.value;

        // a ticked quantity piece starts on its first pair in stock
        data.pieces.forEach((piece, index) => {
            if (piece.quantities && quickAddSetPicked[index]) {
                quickAddSetPieceOptions[index] = quickAddPieceOption(piece, quickAddSetColor);
            }
        });
        quickAddSetSize = (quickAddFirstSetSize(quickAddSetColor) || quickAddSetSizes()[0]).value;
    } else {
        const firstVariant = data.variants.find(variant => variant.available) || data.variants[0];
        quickAddSelected = [...firstVariant.options];
        quickAddShowPriceOverride = Boolean(data.price_override);
    }

    // static text for this card
    dialog.classList.toggle("set", data.type === "set");
    document.querySelector("#quickAddBodyHeaderTextTitle").textContent = data.title;
    document.querySelector("#quickAddBodyFooterLink").href = data.url;
    document.querySelector("#quickAddMediaImage").alt = data.title;

    quickAddRender();

    if (!dialog.open) {
        dialog.showModal();
    }
    document.querySelector("body").classList.add("noScroll");
}

function closeQuickAdd() {
    document.querySelector("#quickAdd").close();
}

// the mini cart keeps the page locked while it is still open underneath
document.querySelector("#quickAdd").addEventListener("close", function () {
    document.querySelector("#quickAddBodyHeader").classList.remove("expand");
    if (document.querySelector("#miniCart")?.open) {
        return;
    }
    document.querySelector("body").classList.remove("noScroll");
});

/********************* Events *********************/

// one delegated handler for the swatches and pills
document.querySelector("#quickAddBodyOptions").addEventListener("click", function (e) {
    const choice = e.target.closest(".quickAddBodyOptionsGroupSwatchesItem, .quickAddBodyOptionsGroupPillsItem");
    if (!choice || choice.disabled) {
        return;
    }

    // set mode: a new colour keeps the size when the ticked pieces have it, otherwise moves to the first size that does
    if (quickAddData.type === "set") {
        if (choice.dataset.group === "color") {
            quickAddSetColor = choice.dataset.value;
            quickAddSetCheckPieceOptions();
            if (!quickAddSetAvailable(quickAddSetSize, quickAddSetColor)) {
                quickAddSetSize = quickAddFirstSetSize(quickAddSetColor)?.value ?? quickAddSetSize;
            }
        } else {
            quickAddSetSize = choice.dataset.value;
        }
    } else {
        quickAddSelectValue(Number(choice.dataset.index), choice.dataset.value);
        quickAddShowPriceOverride = false;
    }

    // the groups are rebuilt, so put keyboard focus back on the same choice
    let choiceSelector = `[data-index="${CSS.escape(choice.dataset.index || "")}"][data-value="${CSS.escape(choice.dataset.value)}"]`;
    if (choice.dataset.group) {
        choiceSelector = `[data-group="${CSS.escape(choice.dataset.group)}"][data-value="${CSS.escape(choice.dataset.value)}"]`;
    }
    quickAddRender();
    document.querySelector("#quickAddBodyOptions").querySelector(choiceSelector)?.focus();
});

// tick or untick a set piece
document.querySelector("#quickAddBodyPiecesList").addEventListener("change", function (e) {
    const input = e.target.closest(".quickAddBodyPiecesListItemInput");
    if (!input) {
        return;
    }
    const index = Number(input.dataset.index);
    quickAddSetPicked[index] = input.checked;

    // a quantity piece ticked in starts on its first pair in stock
    const piece = quickAddData.pieces[index];
    if (piece.quantities && input.checked) {
        quickAddSetPieceOptions[index] = quickAddPieceOption(piece, quickAddSetColor);
    }

    // a piece ticked back in can be out in the current size, so move to the first size the ticked pieces share
    if (!quickAddSetAvailable(quickAddSetSize, quickAddSetColor)) {
        quickAddSetSize = quickAddFirstSetSize(quickAddSetColor)?.value ?? quickAddSetSize;
    }
    quickAddRender();

    // the list is rebuilt, so put keyboard focus back on the same box
    document.querySelector(`.quickAddBodyPiecesListItemInput[data-index="${index}"]`).focus();
});

// pick a size and count for a quantity piece
document.querySelector("#quickAddBodyPiecesList").addEventListener("click", function (e) {
    const pill = e.target.closest(".quickAddBodyOptionsGroupPillsItem");
    if (!pill || pill.disabled) {
        return;
    }
    const index = Number(pill.dataset.index);
    const piece = quickAddData.pieces[index];
    quickAddSetPieceOptions[index] = quickAddPieceOptions(piece)[Number(pill.dataset.option)];
    quickAddRender();

    // the list is rebuilt, so put keyboard focus back on the same pill
    document.querySelector(`#quickAddBodyPiecesList [data-index="${index}"][data-option="${pill.dataset.option}"]`).focus();
});

// add the variant, or each ticked set piece as its own line
async function addQuickAdd(button) {
    button.classList.add("loading");

    let response = null;
    if (quickAddData.type === "set") {
        const items = [];
        quickAddSetPickedPieces().forEach(piece => {
            items.push({
                id: quickAddPieceVariant(piece, quickAddSetSize, quickAddSetColor).id,
                quantity: quickAddPieceQuantity(piece),
            });
        });

        // no form, so post the items as json like main-product-v12
        response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({items})
        }).then(result => result.json());
    } else {
        // addProduct posts the body as is, so it needs FormData
        const formData = new FormData();
        formData.append("id", quickAddFindVariant(quickAddSelected).id);
        formData.append("quantity", 1);
        response = await addProduct(formData);
    }

    button.classList.remove("loading");

    // shopify answers a failed add (e.g. sold out since the page loaded) with a status and description
    if (response.status) {
        button.disabled = true;
        button.textContent = response.description || document.querySelector("#quickAdd").dataset.soldOutText;
        return;
    }

    closeQuickAdd();
    await quickAddOnAdded?.();
}

// mobile image grows/shrinks with tap
document.querySelector('#quickAddBodyHeaderImage').addEventListener("click", function () {
    const header = this.closest("#quickAddBodyHeader");
    if (header) {
        header.classList.toggle("expand");
    }
});

document.querySelector("#quickAddBodyFooterATC").addEventListener("click", function () {
    if (this.disabled || this.classList.contains("loading")) {
        return;
    }
    addQuickAdd(this);
});

document.querySelector("#quickAddBodyHeaderClose").addEventListener("click", closeQuickAdd);

// close on backdrop click
document.querySelector("#quickAdd").addEventListener("click", function (e) {
    if (e.target === this) {
        closeQuickAdd();
    }
});